import { useFrame } from '@react-three/fiber'
import { useMemo } from 'react'
import { QuadraticBezierCurve3, Vector3 } from 'three'

import type { Direction } from '@config'
import { getEdge, getVehicleById, parsePort } from '@engine'
import { getAllVehicleRefs, useLevelGeometry } from '@hooks'
import { useGameStore } from '@stores'

const DIRECTION_OFFSETS: Record<Direction, Vector3> = {
  N: new Vector3(0, 0, -0.5),
  S: new Vector3(0, 0, 0.5),
  E: new Vector3(0.5, 0, 0),
  W: new Vector3(-0.5, 0, 0),
}

const CENTER = new Vector3(0, 0, 0)

export function VehicleKinematicsDriver() {
  const { size } = useLevelGeometry()

  const pool = useMemo(
    () => ({
      position: new Vector3(),
      tangent: new Vector3(1, 0, 0),
      lookAtTarget: new Vector3(),
      cellWorldPos: new Vector3(),
      posA: new Vector3(),
      posB: new Vector3(),
      curve: new QuadraticBezierCurve3(new Vector3(), CENTER, new Vector3()),
    }),
    [],
  )

  useFrame(() => {
    const state = useGameStore.getState()
    if (!state.network || !state.level) return

    const refs = getAllVehicleRefs()
    const { position, tangent, lookAtTarget, cellWorldPos, posA, posB, curve } = pool
    const offsetX = (size.fullWidth - 1) / 2
    const offsetZ = (size.height - 1) / 2

    for (const [id, ref] of refs) {
      if (!ref.current) continue

      const vehicle = getVehicleById(state.network, id)
      if (!vehicle) continue

      const edge = getEdge(state.network, vehicle.edgeId)
      if (!edge) continue

      const portA = parsePort(edge.a)
      const portB = parsePort(edge.b)

      const u = edge.length === 0 ? vehicle.t : vehicle.t / edge.length
      const curveU = vehicle.direction === 1 ? u : 1 - u

      if (edge.type === 'internal') {
        cellWorldPos.set(portA.x - offsetX, 0, portA.y - offsetZ)

        const start = DIRECTION_OFFSETS[portA.direction]
        const end = DIRECTION_OFFSETS[portB.direction]
        curve.v0.copy(start)
        curve.v2.copy(end)

        curve.getPoint(curveU, position).add(cellWorldPos)
        curve.getTangent(curveU, tangent).normalize()
        if (vehicle.direction === -1) tangent.negate()
      } else {
        posA.set(portA.x - offsetX, 0, portA.y - offsetZ).add(DIRECTION_OFFSETS[portA.direction])
        posB.set(portB.x - offsetX, 0, portB.y - offsetZ).add(DIRECTION_OFFSETS[portB.direction])

        position.lerpVectors(posA, posB, curveU)
        tangent.subVectors(posB, posA).normalize()
        if (vehicle.direction === -1) tangent.negate()
      }

      ref.current.position.copy(position)
      if (tangent.lengthSq() > 0.001) {
        ref.current.lookAt(lookAtTarget.copy(position).add(tangent))
      }
    }
  })

  return null
}
